import type { Components } from "react-markdown";
import { memo, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useWorker } from "../../../../hooks/useWorker";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { lighten, styled } from "@mui/material/styles";
import { EXTENSIONS, parseFencedDirective } from "./extensions";
import {
  EvidenceTokenMark,
  RECOGNIZER_BY_SLUG,
  rehypeEvidenceTokens,
  type EvidenceTokenSlug,
} from "./evidence_tokens";
import { parseHeadings } from "./headings";
import { TableOfContents } from "./TableOfContents";

interface QuoteRangeInput {
  quote: string | null;
  text: string;
}

interface QuoteRangeResult {
  start: number;
  end: number;
}

const QUOTE_RANGE_WORKER_URL = new URL("./resolveQuoteRange.worker.ts", import.meta.url);

interface Props {
  markdownText: string;
  /** LLM-reported evidence quote for the active field, or null if none. Resolved to a character range at render time. */
  quote: string | null;
  /** Document title, rendered as the sheet header. Omit to render the body only. */
  title?: string;
  /** Source URL, rendered as a link under the title. */
  sourceUrl?: string;
  /** Optional node rendered in the header, right after the source URL (e.g. metadata badges).
   *  Memoize it at the call site — this component is memoized and a fresh node defeats that. */
  headerExtra?: ReactNode;
}

const DocumentCanvas = styled("div")(({ theme }) => ({
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: theme.palette.divider,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const DocumentSheet = styled("article")(({ theme }) => ({
  flex: "0 1 800px",
  maxWidth: 800,
  minWidth: 0,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 3,
  boxShadow: "0 1px 3px rgba(30,45,60,0.1)",
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
  },
}));

const TitleSection = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DocumentTitle = styled("h1")(({ theme }) => ({
  fontWeight: 600,
  lineHeight: 1.25,
  fontSize: "2.125rem",
  margin: 0,
  fontFamily: theme.typography.fontFamily,
}));

const SourceLink = styled(Link)(({ theme }) => ({
  marginTop: theme.spacing(0.75),
  color: theme.palette.text.disabled,
  textDecoration: "none",
  wordBreak: "break-all",
  "&:hover": { textDecoration: "underline" },
}));

const DocumentBody = styled("div")(({ theme }) => ({
  overflowWrap: "anywhere",
  lineHeight: 1.75,
  "& mark": {
    backgroundColor: theme.highlight.background,
    color: "inherit",
    borderRadius: "2px",
  },
  "& h2": {
    fontSize: "1.3rem",
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
    scrollMarginTop: "8px",
  },
  "& h3": {
    fontSize: "1.1rem",
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    scrollMarginTop: "8px",
  },
  "& h4": {
    fontSize: "1rem",
    fontWeight: 600,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(0.75),
    scrollMarginTop: "8px",
  },
  "& p": { marginTop: theme.spacing(1.5), marginBottom: theme.spacing(1.5), fontSize: "1rem" },
  "& img": { maxWidth: "100%", height: "auto" },
  "& figure": {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    marginLeft: 0,
    marginRight: 0,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: lighten(theme.palette.info.light, 0.9),
    overflowX: "auto",
  },
  "& figcaption": {
    fontSize: "0.85em",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    lineHeight: 1.5,
  },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    border: "solid 1px",
    tableLayout: "fixed",
    backgroundColor: theme.palette.background.paper,
  },
  "& th, & td": {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75),
    overflowWrap: "anywhere",
    textAlign: "left",
  },
  "& th": { backgroundColor: theme.palette.action.hover, fontWeight: 600 },
  "& pre": { whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  "& blockquote": {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderColor: theme.palette.primary.main,
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

/**
 * Renders a document as a centered "page" (white sheet on the canvas) with a sticky table-of-
 * contents rail, PMC-style section/figure/table styling, and a single evidence-span highlight
 * injected as a `<mark>` element that is scrolled into view whenever the quote changes.
 * Fenced pmc-figure and pmc-table directives are rendered as styled blocks via EXTENSIONS.
 */
export const MarkdownWithHighlight = memo(function MarkdownWithHighlight({
  markdownText,
  quote,
  title,
  sourceUrl,
  headerExtra,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const workerInput = useMemo<QuoteRangeInput>(
    () => ({ quote, text: markdownText }),
    [quote, markdownText]
  );
  const range = useWorker<QuoteRangeInput, QuoteRangeResult | null>(
    QUOTE_RANGE_WORKER_URL,
    workerInput
  );

  const processedMarkdown = useMemo(
    () => injectHighlight(markdownText, range),
    [markdownText, range]
  );
  const headings = useMemo(() => parseHeadings(markdownText), [markdownText]);

  useEffect(() => {
    const el = containerRef.current?.querySelector("mark");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [range?.start, range?.end]);

  // TOC entry index maps 1:1 to the rendered h2–h4 element order (see parseHeadings).
  const jumpToHeading = useCallback((index: number) => {
    const target = bodyRef.current?.querySelectorAll("h2, h3, h4")[index];
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <DocumentCanvas ref={containerRef}>
      {headings.length > 1 && <TableOfContents headings={headings} onJump={jumpToHeading} />}

      <DocumentSheet>
        {title && (
          <TitleSection>
            <DocumentTitle>{title}</DocumentTitle>
            {sourceUrl && (
              <SourceLink
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
              >
                {sourceUrl}
              </SourceLink>
            )}
            {headerExtra}
          </TitleSection>
        )}

        <DocumentBody ref={bodyRef}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeEvidenceTokens]}
            components={markdownComponents}
          >
            {processedMarkdown}
          </ReactMarkdown>
        </DocumentBody>
      </DocumentSheet>
    </DocumentCanvas>
  );
});

interface TokenSpanProps {
  "data-evidence-token"?: string;
  className?: string;
  children?: ReactNode;
}

const markdownComponents: Components = {
  code(props) {
    const { className, children } = props;
    if (typeof children === "string" && className?.startsWith("language-")) {
      const lang = className.slice("language-".length);
      const ext = EXTENSIONS.find((e) => e.language === lang);
      if (ext) {
        const parsed = parseFencedDirective(children);
        if (parsed) return <ext.Component meta={parsed.meta} body={parsed.body} />;
      }
    }
    return <code className={className}>{children}</code>;
  },
  // Evidence-token markers emitted by rehypeEvidenceTokens carry a data-evidence-token slug; all
  // other spans (raw HTML passed through by rehypeRaw) render plainly.
  span(props) {
    const { "data-evidence-token": slug, className, children } = props as TokenSpanProps;
    if (slug && slug in RECOGNIZER_BY_SLUG) {
      return <EvidenceTokenMark slug={slug as EvidenceTokenSlug}>{children}</EvidenceTokenMark>;
    }
    return <span className={className}>{children}</span>;
  },
};

/** Returns true when the character range [start, end) overlaps any ``` fence in text. */
function rangeIntersectsFence(text: string, start: number, end: number): boolean {
  const fenceRe = /^```/gm;
  const fences: number[] = [];
  let m;
  while ((m = fenceRe.exec(text)) !== null) {
    fences.push(m.index);
  }
  for (let i = 0; i + 1 < fences.length; i += 2) {
    const fenceStart = fences[i];
    const fenceEnd = fences[i + 1] + text.indexOf("\n", fences[i + 1]);
    if (start < fenceEnd && end > fenceStart) return true;
  }
  return false;
}

/**
 * Injects a single `<mark>` tag at the given character range of the Markdown string.
 * Skips the injection when the range intersects a ``` fence to avoid corrupting directives.
 */
function injectHighlight(text: string, range: { start: number; end: number } | null): string {
  if (!range) return text;
  const { start, end } = range;
  if (start < 0 || end > text.length || start >= end) return text;
  if (rangeIntersectsFence(text, start, end)) return text;
  return `${text.slice(0, start)}<mark>${text.slice(start, end)}</mark>${text.slice(end)}`;
}
